import { useCallback, useEffect, useRef } from 'react';

/**
 * Returns a stable function that delays invoking `callback` by `delayMs`,
 * collapsing a burst of calls into the last one (trailing edge).
 * `delayMs` = 0 invokes synchronously; a pending call is dropped on unmount.
 *
 * Complements `useDebounce`, which debounces a value — use this when the
 * side effect (not the rendered value) must be debounced.
 */
export function useDebouncedCallback<Args extends unknown[]>(
    callback: (...args: Args) => void,
    delayMs: number,
): (...args: Args) => void {
    const callbackRef = useRef(callback);
    const timerRef = useRef<ReturnType<typeof setTimeout>>();

    useEffect(() => {
        callbackRef.current = callback;
    });

    useEffect(() => () => clearTimeout(timerRef.current), []);

    return useCallback(
        (...args: Args) => {
            clearTimeout(timerRef.current);
            if (delayMs > 0) {
                timerRef.current = setTimeout(() => callbackRef.current(...args), delayMs);
            } else {
                callbackRef.current(...args);
            }
        },
        [delayMs],
    );
}
